import React, { FC, RefObject, useContext, useEffect, useId, useLayoutEffect, useState } from 'react';
import './Modal.scss';
import { createPortal } from 'react-dom';
import { CloseButton, Modal as MantineModal, useMantineEnv } from '@mantine/core';
import clsx from 'clsx';
import { ModalSize } from '../../types/ModalSize';
import type { ModalCloseReason } from '../../types/ModalCloseReason';
import { useLatest } from '../../hooks/useLatest';
import { pushModalLayer } from '../../lib/modalLayers';
import { usePortalTarget } from '../../provider/UiContext';
import { FieldA11yContext } from '../../context/FieldA11yContext';
import { ActionModalHeightContext } from './ActionModalHeightContext';
import { DialogAria } from './DialogAria';

export interface IModalViewProps {
    /** Контент модалки */
    children: React.ReactNode;
    /** Закрытие окна: крестик, Escape и клик по фону. Без него окно закрывается только своими кнопками. */
    onClose?: (reason: ModalCloseReason) => void;
    /** Контент для шапки в модальном окне. Строка или свой заголовок — в `<h2>`, им же окно подписано для диктора;
     * с `aria-labelledby` — своя разметка шапки (заголовок со своими кнопками) без обёртки `<h2>`. */
    header?: React.ReactNode;
    /** id заголовка в своей разметке `header` или в содержимом окна (окно без шапки со своим заголовком): окно
     * подписано им. */
    'aria-labelledby'?: string;
    /** Имя окна для диктора, когда видимого заголовка нет (окно поиска без шапки). */
    'aria-label'?: string;
    /** id описания окна (через пробел — несколько): текст вопроса, который диктор читает вслед за именем. По
     * умолчанию описания нет — содержимое читается по мере перехода по нему. */
    'aria-describedby'?: string;
    /** Контент для футера в модальном окне */
    footer?: React.ReactNode;
    /** Ширина модалки
     * @default xl
     */
    size?: ModalSize;
    /** Произвольная ширина (px или CSS-значение). Перекрывает size. */
    width?: number | string;
    /** Класс на root-элементе */
    className?: string;
    /** Закрывать по Escape (при `onClose`). Escape закрывает только окно, в котором фокус; погашенный вложенным
     * контролом (редактор ячейки, открытый список) окно не закрывает. Окну с большим несохранённым вводом —
     * вопрос перед закрытием (`onClose` спрашивает) или `false`.
     * @default true
     */
    closeOnEscape?: boolean;
    /** Закрывать по клику по фону (при `onClose`).
     * @default true
     */
    closeOnClickOutside?: boolean;
    /** Заблокировать все средства закрытия (крестик, Escape, клик снаружи).
     * Используется на время выполнения сабмита/подписания/загрузки,
     * чтобы пользователь не оборвал операцию.
     * @default false
     */
    closeDisabled?: boolean;
    /** Показывать крестик в шапке. По умолчанию — true, если задан onClose. */
    withCloseButton?: boolean;
    /** Что получает фокус при открытии, если фокус ещё не внутри окна (поле с `autoFocus`). По умолчанию — элемент
     * с `data-autofocus`, иначе первый доступный с клавиатуры. После закрытия фокус возвращается на элемент,
     * открывший окно. */
    initialFocus?: RefObject<HTMLElement | null>;
    /** z-index модалки
     * @default 1000
     */
    zIndex?: number;
    /** Открыта ли модалка.
     * Если проп не задан, атом сам проигрывает появление при монтировании, но закрытие
     * анимировать не может: вызывающий рендерит `{isOpen && <Modal/>}` и вырывает
     * поддерево мгновенно. Чтобы анимировалось и закрытие — передайте `opened`
     * и держите Modal смонтированным.
     */
    opened?: boolean;
    /** Во весь экран: без отступов, рамки и скругления. */
    fullScreen?: boolean;
    /** Положение окна по вертикали: `top` — у верхнего края экрана (на `yOffset`): окно, высота которого меняется на
     * глазах (поиск с выдачей), не прыгает.
     * @default center
     */
    align?: 'center' | 'top';
    /** Отступ окна от верхнего и нижнего края экрана (число — px): у `align="top"` — где окно стоит; высота окна — не
     * больше экрана без двух отступов.
     * @default 5dvh
     */
    yOffset?: number | string;
    /** Если true — children рендерятся без обёртки `rf-modal__wrapper` и без footer.
     * Используется для нестандартной компоновки (например, ChatAIForm c sidebar).
     * @default false
     */
    noFrame?: boolean;
}

const SIZE_MAP: Record<ModalSize, number | string> = {
    s: 384,
    m: 488,
    l: 592,
    xl: 844,
    xxl: 1240,
    max: '95%',
    content: 'auto',
};

const MODAL_TRANSITION = { transition: 'pop', duration: 180 } as const;

// токен — палитра Front (и custom.css конфигураций); в админке его нет
const RADIUS = 'var(--rf-modal-border-radius, 20px)';

const CLASS_NAMES = {
    overlay: 'rf-modal__bg',
    content: 'rf-modal__container',
    header: 'rf-modal__header',
    title: 'rf-modal__title',
    close: 'rf-modal__close-button',
    body: 'rf-modal__body',
};

const noop = () => undefined;

const AUTOFOCUS = 'data-autofocus';

/**
 * Начальный фокус окна. Ловушка фокуса Mantine после монтирования переводит фокус на `[data-autofocus]` или первый
 * доступный элемент — поле с `autoFocus` теряло бы фокус (на крестик). Метка ставится до этого, при вставке окна в
 * документ: компонент рендерится последним в теле окна, и `autoFocus` детей к этому моменту уже сработал.
 */
const InitialFocus: FC<{ modalId: string; initialFocus?: RefObject<HTMLElement | null> }> = ({
    modalId,
    initialFocus,
}) => {
    useLayoutEffect(() => {
        const active = document.activeElement;
        const target =
            active instanceof HTMLElement && document.getElementById(modalId)?.contains(active)
                ? active
                : initialFocus?.current;
        if (!target || target.hasAttribute(AUTOFOCUS)) {
            target?.focus({ preventScroll: true });
            return;
        }
        target.setAttribute(AUTOFOCUS, 'true');
        target.focus({ preventScroll: true });
        return () => target.removeAttribute(AUTOFOCUS);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- только при вставке окна в документ
    }, []);
    return null;
};

const ModalView: FC<IModalViewProps> = ({
    children,
    onClose,
    header,
    'aria-labelledby': labelledBy,
    'aria-label': ariaLabel,
    'aria-describedby': describedBy,
    footer,
    size = 'xl',
    width,
    className = '',
    closeOnEscape = true,
    closeOnClickOutside = true,
    closeDisabled = false,
    withCloseButton,
    initialFocus,
    zIndex = 1000,
    opened,
    fullScreen = false,
    align = 'center',
    yOffset,
    noFrame = false,
}) => {
    const fullHeight = useContext(ActionModalHeightContext);
    const portalTarget = usePortalTarget();
    const instantTransitions = useMantineEnv() === 'test';
    // Mantine инициализирует переход как `entered`, если mounted=true уже на первом
    // рендере, и пропускает его (useDidUpdate). Стартуем закрытыми и открываемся следующим
    // кадром. rAF, а не useEffect: Portal монтирует детей (и Transition) из layout-эффекта,
    // а React перед этим ре-рендером домывает passive-эффекты — флип из useEffect попал бы
    // в тот же коммит, и Transition снова родился бы сразу открытым.
    // В тестовом окружении переходов нет — окно открыто сразу.
    const [selfOpened, setSelfOpened] = useState(instantTransitions);
    useEffect(() => {
        if (instantTransitions) return;
        const raf = requestAnimationFrame(() => setSelfOpened(true));
        return () => cancelAnimationFrame(raf);
    }, [instantTransitions]);
    const wantOpened = opened ?? true;
    const isOpened = opened ?? selfOpened;

    // Кто открыл окно — на рендере, пока фокус ещё на нём: `autoFocus` поля окна переносит фокус уже при вставке.
    const [opening, setOpening] = useState(() => ({
        opened: wantOpened,
        opener: wantOpened ? document.activeElement : null,
    }));
    if (opening.opened !== wantOpened) {
        setOpening({ opened: wantOpened, opener: wantOpened ? document.activeElement : opening.opener });
    }
    const { opener } = opening;

    // id корня Mantine (`ModalBase`): окно находим в DOM и до привязки ref — в том же коммите, где оно вставлено
    const modalId = useId();
    const canClose = Boolean(onClose) && !closeDisabled;
    const showCloseButton = withCloseButton ?? canClose;
    const ownHeader = Boolean(labelledBy);
    const hasHeader = Boolean(header) || showCloseButton;
    const hasContent = React.Children.count(children) > 0;
    const effectiveSize = hasContent || size === 's' ? size : 'm';
    const mantineSize = width ?? SIZE_MAP[effectiveSize];

    const escape = useLatest(canClose && closeOnEscape ? onClose : undefined);
    useEffect(() => {
        if (!wantOpened) return;
        const layerRoot = () => document.getElementById(modalId);
        return pushModalLayer({
            root: layerRoot,
            dialog: () => layerRoot()?.querySelector<HTMLElement>('.rf-modal__container') ?? null,
            onEscape: () => {
                const close = escape.current;
                close?.('escape');
                return Boolean(close);
            },
        });
    }, [wantOpened, escape, modalId]);

    /** После закрытия — фокус на элемент, открывший окно: вместе с окном фокус ушёл бы в body. Если он уже на
     *  другом элементе (обработчик открыл следующее окно или поле), не трогаем. */
    useEffect(() => {
        if (!wantOpened) return;
        return () => {
            const active = document.activeElement;
            if (
                (!active || active === document.body || document.getElementById(modalId)?.contains(active)) &&
                opener instanceof HTMLElement &&
                opener !== document.body &&
                opener.isConnected
            ) {
                opener.focus({ preventScroll: true });
            }
        };
    }, [wantOpened, opener, modalId]);

    const initialFocusMark = <InitialFocus modalId={modalId} initialFocus={initialFocus} />;

    const modal = (
        <ActionModalHeightContext value={false}>
            <MantineModal.Root
                id={modalId}
                opened={isOpened}
                onClose={canClose ? () => onClose!('outside') : noop}
                size={mantineSize}
                padding={0}
                radius={RADIUS}
                centered={align === 'center'}
                fullScreen={fullScreen}
                yOffset={yOffset}
                closeOnEscape={false}
                closeOnClickOutside={canClose && closeOnClickOutside}
                returnFocus={false}
                zIndex={zIndex}
                withinPortal={!portalTarget}
                transitionProps={MODAL_TRANSITION}
                classNames={{
                    root: clsx(
                        'rf-modal',
                        `rf-modal--${effectiveSize}`,
                        fullHeight && 'rf-modal--full-height',
                        !hasContent && 'rf-modal--contentless',
                        className,
                    ),
                    ...CLASS_NAMES,
                }}
                data-testid="rf-modal"
            >
                <MantineModal.Overlay />
                <MantineModal.Content radius={RADIUS} aria-label={ariaLabel}>
                    {hasHeader && (
                        <MantineModal.Header>
                            {header ? (
                                ownHeader ? (
                                    <div className="rf-modal__header-content">{header}</div>
                                ) : (
                                    <MantineModal.Title>{header}</MantineModal.Title>
                                )
                            ) : null}
                            {showCloseButton && (
                                <CloseButton
                                    className={CLASS_NAMES.close}
                                    aria-label="Закрыть"
                                    disabled={closeDisabled}
                                    onClick={() => canClose && onClose?.('close-button')}
                                />
                            )}
                        </MantineModal.Header>
                    )}
                    <MantineModal.Body>
                        {noFrame ? (
                            children
                        ) : (
                            <div className="rf-modal__wrapper">
                                {hasContent && <div className="rf-modal__content">{children}</div>}
                                {footer && <div className="rf-modal__footer">{footer}</div>}
                            </div>
                        )}
                        <DialogAria rootId={modalId} labelledBy={labelledBy} describedBy={describedBy} />
                        {initialFocusMark}
                    </MantineModal.Body>
                </MantineModal.Content>
            </MantineModal.Root>
        </ActionModalHeightContext>
    );
    // Окно, открытое из поля группы (окно выбора пикера), не наследует её подпись и сообщение — у полей окна свои
    const scoped = <FieldA11yContext value={null}>{modal}</FieldA11yContext>;
    // Хост области (дизайнер маршрутов) — свой портал: Portal Mantine в тестовом окружении рендерит на месте,
    // а окно области должно оказаться в её хосте и там (тема, стили области).
    return portalTarget ? createPortal(scoped, portalTarget) : scoped;
};

export default ModalView;
